import { median } from "./util";
import type { SkillKey } from "./types";

/**
 * Personal goals: targets a player sets for themselves (or takes from a suggestion), measured against
 * their own history rather than other players. Pure: the server gathers the stats, this decides progress.
 */
export type GoalKind = "level" | "accuracy" | "pace" | "volume" | "streak";

export interface GoalSpec { kind: GoalKind; skill?: SkillKey; target: number }
export interface Goal extends GoalSpec { baseline: number }

/** What the server measures for one goal. `recent` is that skill's latest answers, oldest first. */
export interface GoalStats {
  rating: number;
  recent: { correct: boolean; ratio: number }[];
  streak: number;
  /** Problems answered since the goal was set. */
  answeredSince: number;
}

/** Answers considered for accuracy and pace, and the fewest needed before they count. */
export const GOAL_WINDOW = 30;
export const GOAL_MIN_SAMPLE = 20;

export const GOAL_LIMITS: Record<GoalKind, { min: number; max: number; step: number }> = {
  level: { min: 2, max: 10, step: 1 },
  accuracy: { min: 60, max: 100, step: 5 },
  /** Percent of the target time: 90 means "usually answer in 90% of the target time or faster". */
  pace: { min: 50, max: 150, step: 5 },
  volume: { min: 20, max: 1000, step: 10 },
  streak: { min: 2, max: 365, step: 1 },
};

export interface GoalProgress { value: number; fraction: number; done: boolean; text: string }

export function goalProgress(g: Goal, s: GoalStats): GoalProgress {
  const clampF = (x: number) => Math.max(0, Math.min(1, x));
  switch (g.kind) {
    case "level": {
      const span = Math.max(0.5, g.target - g.baseline);
      return { value: s.rating, fraction: clampF((s.rating - g.baseline) / span), done: s.rating >= g.target, text: `level ${s.rating.toFixed(1)} of ${g.target}` };
    }
    case "accuracy": {
      const w = s.recent.slice(-GOAL_WINDOW), n = w.length;
      const acc = n ? (100 * w.filter((x) => x.correct).length) / n : 0;
      const enough = n >= GOAL_MIN_SAMPLE;
      return { value: acc, fraction: enough ? clampF(acc / g.target) : clampF((acc / g.target) * (n / GOAL_MIN_SAMPLE)), done: enough && acc >= g.target,
        text: enough ? `${Math.round(acc)}% of your last ${n}` : `${n} of ${GOAL_MIN_SAMPLE} answers so far` };
    }
    case "pace": {
      const w = s.recent.slice(-GOAL_WINDOW), n = w.length;
      const pace = n ? 100 * median(w.map((x) => Math.min(3, x.ratio))) : 0;
      const enough = n >= GOAL_MIN_SAMPLE;
      return { value: pace, fraction: enough ? clampF(g.target / Math.max(pace, 1)) : clampF((n / GOAL_MIN_SAMPLE) * 0.5), done: enough && pace <= g.target,
        text: enough ? `typically ${Math.round(pace)}% of target time` : `${n} of ${GOAL_MIN_SAMPLE} answers so far` };
    }
    case "volume":
      return { value: s.answeredSince, fraction: clampF(s.answeredSince / g.target), done: s.answeredSince >= g.target, text: `${Math.min(s.answeredSince, g.target)} of ${g.target} problems` };
    case "streak":
      return { value: s.streak, fraction: clampF(s.streak / g.target), done: s.streak >= g.target, text: `${s.streak} of ${g.target} days` };
  }
}

export function describeGoal(g: GoalSpec, skillName?: string): string {
  const sk = skillName ?? g.skill ?? "";
  switch (g.kind) {
    case "level": return `Reach level ${g.target} in ${sk}`;
    case "accuracy": return `Get ${g.target}% right in ${sk}`;
    case "pace": return g.target <= 100 ? `Answer ${sk} within the target time` : `Answer ${sk} within ${g.target}% of the target time`;
    case "volume": return `Practice ${g.target} problems`;
    case "streak": return `Keep a ${g.target}-day streak`;
  }
}

export interface SkillSnapshot { skill: SkillKey; rating: number; attempts: number; recent: { correct: boolean; ratio: number }[] }

/**
 * Up to three gentle goals drawn from the player's own numbers: the skill they're weakest in, the one that
 * is least accurate, and a habit goal. Targets are one small step above where they are now.
 * `have` lists goals already active so suggestions never duplicate them.
 */
export function suggestGoals(skills: SkillSnapshot[], streak: number, have: GoalSpec[]): GoalSpec[] {
  const out: GoalSpec[] = [];
  const taken = (g: GoalSpec) => have.some((h) => h.kind === g.kind && h.skill === g.skill) || out.some((o) => o.kind === g.kind && o.skill === g.skill);
  const add = (g: GoalSpec) => { if (!taken(g)) out.push(g); };
  const byRating = [...skills].sort((a, b) => a.rating - b.rating);
  for (const s of byRating) {
    const target = Math.min(10, Math.max(2, Math.floor(s.rating) + 1));
    if (s.rating < 10) { const g: GoalSpec = { kind: "level", skill: s.skill, target }; if (!taken(g)) { out.push(g); break; } }
  }
  const sampled = skills.filter((s) => s.recent.length >= GOAL_MIN_SAMPLE)
    .map((s) => ({ s, acc: s.recent.slice(-GOAL_WINDOW).filter((x) => x.correct).length / Math.min(GOAL_WINDOW, s.recent.length) }))
    .sort((a, b) => a.acc - b.acc);
  if (sampled.length && sampled[0].acc < 0.85) add({ kind: "accuracy", skill: sampled[0].s.skill, target: 85 });
  else if (sampled.length) {
    const slow = sampled.map((x) => ({ s: x.s, pace: median(x.s.recent.slice(-GOAL_WINDOW).map((r) => Math.min(3, r.ratio))) })).sort((a, b) => b.pace - a.pace)[0];
    if (slow.pace > 0.95) add({ kind: "pace", skill: slow.s.skill, target: 90 });
  }
  const next = [3, 7, 14, 30, 60, 100, 365].find((m) => m > streak) ?? 365;
  add(streak < 3 && skills.every((s) => s.attempts < 30) ? { kind: "volume", target: 50 } : { kind: "streak", target: next });
  return out.slice(0, 3);
}
